import type { OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';
import { AlbumCaseTypeEnum } from 'src/app/shared/models/album-case.interface';
import type { AlbumInterface } from 'src/app/shared/models/album.interface';
import type { InstanceInterface } from 'src/app/shared/models/instance.interface';
import type { SerieInterface } from 'src/app/shared/models/serie.interface';
import { AppStateService } from 'src/app/shared/services/app-state.service';
import { environment } from '../../../../environments/environment';
import type { AlbumCaseAddInterface } from '../../models/album-case-add.interface';
import { SexEnum, SterilisationEnum } from '../../models/patient.interface';
import { SexAndSterilisation } from '../../models/sexesAndSterilisations';
import { SpeciesInterface } from '../../models/species.interface';
import { AlbumService } from '../../services/album.service';
import { InstanceService } from '../../services/instance.service';
import { SerieService } from '../../services/serie.service';
import { SpeciesService } from '../../services/species.service';
import { StudiesService } from '../../services/studies.service';
import type { IDialogContentComponent } from '../modal-dialog/modal-dialog.service';

@Component({
  selector: 'app-album-cases-add-dialog',
  templateUrl: './album-cases-add-dialog.component.html',
  styleUrls: ['./album-cases-add-dialog.component.scss']
})
export class AlbumCasesAddDialogComponent implements OnInit, IDialogContentComponent<AlbumCaseAddInterface | undefined, AlbumCasesAddDialogParamInterface> {
  public series$: Observable<SerieInterface[]>;
  public albums$: Observable<AlbumInterface[]>;
  public instances: InstanceInterface[] = [];
  public speciesList: SpeciesInterface[];
  SexEnum = SexEnum;
  SterilisationEnum = SterilisationEnum;

  input?: AlbumCasesAddDialogParamInterface;

  sexes: SexEnum[] = [
    SexEnum.Male,
    SexEnum.Female
  ];

  sterilisations: SterilisationEnum[] = [
    SterilisationEnum.NotSterilized,
    SterilisationEnum.Sterilized
  ];

  sexesAndSterilisations: SexAndSterilisation[] = [
    { sex : SexEnum.Male, sterilisation : SterilisationEnum.NotSterilized },
    { sex : SexEnum.Male, sterilisation : SterilisationEnum.Sterilized },
    { sex : SexEnum.Female, sterilisation : SterilisationEnum.NotSterilized },
    { sex : SexEnum.Female, sterilisation : SterilisationEnum.Sterilized }
  ];

  form = new FormGroup({
    serie: new FormControl<undefined | SerieInterface>(undefined),
    name: new FormControl<undefined | string>(undefined, [Validators.required]),
    album: new FormControl<undefined | AlbumInterface>(undefined, [Validators.required]),
    type: new FormControl<undefined | AlbumCaseTypeEnum>(undefined, [Validators.required]),
    instances: new FormControl<number[]>([], [Validators.required]),
    anonymize: new FormControl<boolean>(false),
    cloneAttachment: new FormControl<boolean>(false),
    species: new FormControl<undefined | SpeciesInterface>(undefined, [Validators.required]),
    sex: new FormControl<undefined | SexEnum>(undefined, [Validators.required]),
    sterilisation: new FormControl<undefined | SterilisationEnum>(undefined, [Validators.required]),
  });

  showSerie = true;

  albumCaseTypes: AlbumCaseTypeEnum[] = [AlbumCaseTypeEnum.PATHOLOGIQUE, AlbumCaseTypeEnum.PHYSIOLOGIQUE];
  previewUrl = `${environment.apiUrl}/instance/public-preview/`;

  constructor(
    private readonly serieService: SerieService,
    private readonly albumService: AlbumService,
    private readonly instanceService: InstanceService,
    private readonly appStateService: AppStateService,
    private readonly speciesService: SpeciesService,
    private readonly studiesService: StudiesService
  ) { }

  ngOnInit(): void {

    this.setTrad();
    let studyId: number | undefined;
    if (this.input?.instances) {
      this.instances = this.input.instances;
      studyId = this.input.studyId;
      (this.form.get('instances') as FormControl<number[]>).setValue(this.instances.map(i => i.internalid));
      this.showSerie = false;
    } else {
      if (this.input?.studyId) {
        studyId = this.input?.studyId;
        (this.form.get('serie') as FormControl<undefined | SerieInterface>).valueChanges.subscribe(serie => {
          if (serie) {
            this.instanceService.getInstances(serie.publicId).subscribe(instances => this.instances = instances);
          }
        });

        this.series$ = this.serieService.getSeriesByStudy(this.input.studyId).pipe(tap(series => {
          if (series.length) {
            this.form.get('serie')?.setValue(series[0]);
          }
        }));

      }
    }


    this.speciesService.getSpecies().subscribe(species => {
      this.speciesList = species;

      if (studyId) {
        this.studiesService.getStudyDetails(studyId).subscribe(study => {
          if (study.patientSex) {

            const sex = study.patientSex.toString() === 'MALE' ? SexEnum.Male : SexEnum.Female;
            const sexFound = this.sexes.find(s => s === sex);
            if (sexFound !== undefined) {
              this.form.get('sex')?.setValue(sexFound);
              this.form.get('sex')?.disable();
            }
          }

          if (study.patientSterilisation) {
            const sterilisation = study.patientSterilisation.toString() === 'NOTSTERILIZED' ? SterilisationEnum.NotSterilized : SterilisationEnum.Sterilized;
            const sterilisationFound = this.sterilisations.find(s => s === sterilisation);
            if (sterilisationFound !== undefined) {
              this.form.get('sterilisation')?.setValue(sterilisationFound);
              this.form.get('sterilisation')?.disable();
            }
          }

          if (study.speciesId) {
            this.form.get('species')?.setValue(species.find(s => s.id === study.speciesId));
            this.form.get('species')?.disable();
          }
        });
      }
    });


    if (this.appStateService.clinicId) {
      this.albums$ = this.albumService.getAlbums(this.appStateService.clinicId).pipe(
        tap(albums => {
          if (albums.length) {
            this.form.get('album')?.setValue(albums[0]);
          }
        })
      );
    }

    (this.form.get('album') as FormControl).valueChanges.subscribe(_ => {
      this.form.get('cloneAttachment')?.setValue(false);
    });
  }

  isAtlas() {
    return (this.form.get('album') as FormControl)?.value?.type === 'ATLAS';
  }

  getResult(): AlbumCaseAddInterface {
    return {
      albumId: (this.form.get('album') as FormControl).value.id,
      instances: (this.form.get('instances') as FormControl).value,
      name: (this.form.get('name') as FormControl).value,
      type: (this.form.get('type') as FormControl).value,
      keywords: [],
      anonymize: (this.form.get('anonymize') as FormControl).value,
      cloneAttachment: (this.form.get('cloneAttachment') as FormControl).value,
      sex : (this.form.get('sex') as FormControl).value,
      sterilisation: (this.form.get('sterilisation') as FormControl).value,
      speciesId: (this.form.get('species') as FormControl).value.id
    };
  }

  isDisabled(): boolean {
    return this.form.invalid;
  }

  convertAlbumTypeEnum(albumCaseType: AlbumCaseTypeEnum) {
    switch (albumCaseType) {
      case AlbumCaseTypeEnum.PATHOLOGIQUE:
        return 'Pathologique';
      case AlbumCaseTypeEnum.PHYSIOLOGIQUE:
        return 'Physiologique';
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      default:
        return 'Autre';
    }
  }

  private setTrad(): void {
    marker('enum.sterilisation.notsterilized');
    marker('enum.sterilisation.sterilized');
  }
}

export interface AlbumCasesAddDialogParamInterface {
  studyId?: number;
  instances?: InstanceInterface[];
}
